import { Link } from "react-router-dom";
import { ArrowUpRight, Store } from "lucide-react";
import { Layout } from "../components/Layout";
import { WaitlistCapture } from "../components/WaitlistCapture";
import { NUTRITION_CONCEPTS, NUTRITION_IMAGE } from "../lib/nutrition";

const PARTNERS = ["Breeders", "Kennels", "Trainers", "Pet Retailers", "Groomers", "Dog Professionals"];

export default function Shop() {
  return (
    <Layout>
      {/* Hero */}
      <section className="bg-white border-b border-eon-border">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="eon-label mb-3">E.O.N Nutrition™</div>
            <h1 className="eon-heading text-4xl sm:text-5xl lg:text-6xl text-eon-text">Built for the Boerboel.</h1>
            <p className="mt-6 text-base lg:text-lg text-eon-muted max-w-xl leading-relaxed">
              A premium nutrition and wellness collection being developed with the unique needs of powerful, large-breed dogs in mind.
            </p>
            <div className="mt-4 inline-block text-xs font-bold tracking-widest uppercase bg-eon-elevated text-eon-gold rounded-full px-4 py-2">Coming Soon</div>
            <div className="mt-8"><WaitlistCapture subjectType="product_waitlist" subjectId="eon-nutrition" cta="Join the Product Waitlist" /></div>
            <p className="mt-3 text-xs text-eon-muted">Get launch updates — no spam, just the drop.</p>
          </div>
          <div className="rounded-3xl overflow-hidden border border-eon-border bg-eon-surface">
            <img src={NUTRITION_IMAGE} alt="E.O.N Nutrition concept packaging" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Concept collection */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16">
          <div className="eon-label mb-3">The Collection</div>
          <h2 className="eon-heading text-3xl sm:text-4xl text-eon-text">Future product concepts.</h2>
          <p className="text-sm text-eon-muted mt-3 max-w-2xl">Every product below is a concept in development. Prices, ingredients and details will be published once formulations are finalized.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {NUTRITION_CONCEPTS.map((c, i) => (
              <Link key={c.slug} to={`/shop/${c.slug}`} data-testid={`nutrition-card-${c.slug}`}
                className="eon-card group overflow-hidden flex flex-col animate-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="relative aspect-[4/3] bg-eon-surface flex items-center justify-center overflow-hidden">
                  <img src={NUTRITION_IMAGE} alt={c.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute top-3 left-3 text-[10px] font-bold tracking-widest uppercase bg-white/90 text-eon-gold rounded-full px-3 py-1">Coming Soon</span>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="eon-label">{c.category}</div>
                  <h3 className="font-display text-xl font-bold text-eon-text mt-1">{c.name}</h3>
                  <p className="text-sm text-eon-muted mt-2 flex-1">{c.short}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-eon-gold">Learn more <ArrowUpRight size={16} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* B2B Wholesale */}
      <section className="bg-eon-surface border-t border-eon-border">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="eon-label mb-3 flex items-center gap-2"><Store size={16} /> Wholesale</div>
            <h2 className="eon-heading text-3xl sm:text-4xl text-eon-text">Become an E.O.N Retail Partner.</h2>
            <p className="mt-4 text-eon-muted leading-relaxed max-w-lg">When E.O.N Nutrition launches, approved businesses will be able to carry the line. Join the wholesale waitlist to apply early.</p>
            <div className="flex flex-wrap gap-2 mt-5">
              {PARTNERS.map((p) => <span key={p} className="text-xs font-semibold text-eon-text bg-white border border-eon-border rounded-full px-3 py-1.5">{p}</span>)}
            </div>
            <div className="mt-7"><WaitlistCapture subjectType="wholesale_waitlist" subjectId="eon-retail-partner" placeholder="Business email" cta="Join the Wholesale Waitlist" /></div>
          </div>
          <div className="rounded-3xl border border-eon-border bg-white p-8">
            <h3 className="font-display text-lg font-bold text-eon-text">Two revenue engines, one brand.</h3>
            <div className="mt-5 space-y-4">
              <div className="flex items-start gap-3"><span className="font-display font-black text-eon-gold">B2C</span><p className="text-sm text-eon-muted">Direct-to-consumer product revenue from owners and members.</p></div>
              <div className="flex items-start gap-3"><span className="font-display font-black text-eon-gold">B2B</span><p className="text-sm text-eon-muted">Wholesale revenue through breeders, trainers and pet businesses.</p></div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
